<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SidebarLayout from '@/layout/SidebarLayout.vue';
import SecurityTrim from '@/components/ui/SecurityTrim.vue';
import QueueTable from '@/components/aml-review/QueueTable.vue';
import QueuePaginationControls from '@/components/aml-review/QueuePaginationControls.vue';
import type { QueueStatus } from '@/components/aml-review/queueStatus';
import { AmlReviewActions } from '@/integrations/Immersve/auth/actions';

const route = useRoute();
const router = useRouter();

const status = computed<QueueStatus>(() => {
  if (route.params.status === 'completed') return 'completed';
  if (route.params.status === 'processing') return 'processing';
  if (route.params.status === 'canceled') return 'canceled';
  return 'escalated';
});

function selectTab(newStatus: QueueStatus) {
  router.push({ path: `/aml-review/status/${newStatus}`, query: route.query });
}

const queueTable = ref<InstanceType<typeof QueueTable> | null>(null);
</script>

<template>
  <SidebarLayout>
    <SecurityTrim :actions="[AmlReviewActions.Read]" account-id="*">
      <div class="px-8 py-6" data-test="screen-aml-review-queue">
        <h1 class="text-2xl font-bold text-gray-900">AML Review Queue</h1>
        <p class="mt-1 text-sm text-gray-500">
          Profiles flagged for AML review, from agent screening to a human decision.
        </p>
        <div class="mt-4 flex gap-1 border-b border-gray-200">
          <button
            type="button"
            class="-mb-px border-b-2 px-1 py-2.5 text-sm font-semibold"
            :class="
              status === 'escalated'
                ? 'border-indigo-600 text-gray-900'
                : 'border-transparent text-gray-500'
            "
            data-test="tab-needs-review"
            @click="selectTab('escalated')"
          >
            Needs Review
          </button>
          <button
            type="button"
            class="-mb-px border-b-2 px-1 py-2.5 text-sm font-semibold"
            :class="
              status === 'completed'
                ? 'border-indigo-600 text-gray-900'
                : 'border-transparent text-gray-500'
            "
            data-test="tab-completed"
            @click="selectTab('completed')"
          >
            Completed
          </button>
          <button
            type="button"
            class="-mb-px border-b-2 px-1 py-2.5 text-sm font-semibold"
            :class="
              status === 'canceled'
                ? 'border-indigo-600 text-gray-900'
                : 'border-transparent text-gray-500'
            "
            data-test="tab-canceled"
            @click="selectTab('canceled')"
          >
            Canceled
          </button>
          <div class="ml-auto flex items-center gap-4">
            <button
              type="button"
              class="-mb-px border-b-2 px-1 py-2.5 text-sm font-semibold"
              :class="
                status === 'processing'
                  ? 'border-indigo-600 text-gray-900'
                  : 'border-transparent text-gray-500'
              "
              data-test="tab-processing"
              @click="selectTab('processing')"
            >
              Processing
            </button>
            <div v-if="queueTable?.showTopPager" class="flex items-center gap-1.5 pb-2">
              <QueuePaginationControls
                position="top"
                variant="icon"
                :has-previous="queueTable.hasPrevious"
                :has-next="queueTable.hasNext"
                :loading="queueTable.loading"
                @previous="queueTable.goToPreviousPage()"
                @next="queueTable.goToNextPage()"
              />
            </div>
          </div>
        </div>
        <div class="mt-6">
          <QueueTable ref="queueTable" :status="status" />
        </div>
      </div>
    </SecurityTrim>
  </SidebarLayout>
</template>
